import {
  Service,
  ApiResponse,
  ServiceSubServiceLink,
  FollowUpClientsData,
  FollowUpStatusData,
  FollowUpStatusByClientData,
} from "../types";
import { apiClient } from "./apiClient";

export const servicesApi = {
  // Get all services
  async getServicesByCompany(): Promise<ApiResponse<Service[]>> {
    try {
      const data = await apiClient.get<{ data: Service[] } | Service[]>(
        `/services`
      );

      // Handle both response formats (with or without data wrapper)
      const services = Array.isArray(data) ? data : (data as any).data || data;

      return {
        success: true,
        data: services,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Get service by ID
  async getServiceById(serviceId: number): Promise<ApiResponse<Service>> {
    try {
      const data = await apiClient.get<any>(`/services/${serviceId}`);

      return {
        success: true,
        data: data.data || data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get services by professional
  async getServicesByProfessional(
    professionalId: number = 1
  ): Promise<ApiResponse<Service[]>> {
    try {
      const data = await apiClient.get<any>(
        `/services/professional/${professionalId}`
      );

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Create a new service
  async createService(
    serviceData: Omit<Service, "id" | "createdAt" | "updatedAt" | "companyId">
  ): Promise<ApiResponse<Service>> {
    try {
      const data = await apiClient.post<any>("/services", serviceData);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Update an existing service
  async updateService(
    id: number,
    serviceData: Partial<Omit<Service, "id" | "createdAt" | "updatedAt" | "companyId">>
  ): Promise<ApiResponse<Service>> {
    try {
      const data = await apiClient.put<any>(`/services/${id}`, serviceData);

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Delete a service
  async deleteService(id: number): Promise<ApiResponse<boolean>> {
    try {
      await apiClient.delete(`/services/${id}`);

      return {
        success: true,
        data: true,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get service-subservice links for a specific service
  async getServiceSubServiceLinks(
    serviceId: number
  ): Promise<ApiResponse<ServiceSubServiceLink[]>> {
    try {
      const data = await apiClient.get<any>(
        `/service-sub-services/service/${serviceId}/sub-services`
      );

      return {
        success: true,
        data: data.data || data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Create a service-subservice link
  async createServiceSubServiceLink(
    serviceId: number,
    subServiceId: number,
    price: number,
    isRequired?: boolean
  ): Promise<ApiResponse<ServiceSubServiceLink>> {
    try {
      const data = await apiClient.post<any>("/service-sub-services", {
        serviceId,
        subServiceId,
        price,
        isRequired: isRequired || false,
      });

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Update a service-subservice link price
  async updateServiceSubServiceLink(
    relationshipId: number,
    price: number,
    isRequired?: boolean
  ): Promise<ApiResponse<ServiceSubServiceLink>> {
    try {
      const data = await apiClient.put<any>(
        `/service-sub-services/${relationshipId}`,
        { price, isRequired }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /** Clients past service validity who need follow-up (GET /services/follow-up-clients). */
  async getFollowUpClients(): Promise<ApiResponse<FollowUpClientsData>> {
    try {
      const response = await apiClient.get<{
        message: string;
        data: FollowUpClientsData;
      }>("/services/follow-up-clients");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /** Hide a follow-up suggestion for the current last appointment (POST /services/follow-up-ignores). */
  async ignoreFollowUpSuggestion(params: {
    clientId: number;
    targetId: number;
    kind: "service" | "subService";
    lastAppointmentId: number;
  }): Promise<ApiResponse<{ id: number }>> {
    try {
      const response = await apiClient.post<{
        message: string;
        data: { id: number };
      }>("/services/follow-up-ignores", params);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /** Last service dates and expiration for one client or car (GET /services/follow-up-status). */
  async getFollowUpStatus(params: {
    clientId: number;
    clientCarId?: number;
  }): Promise<ApiResponse<FollowUpStatusData>> {
    try {
      const search = new URLSearchParams({
        clientId: String(params.clientId),
      });
      if (params.clientCarId != null) {
        search.set("clientCarId", String(params.clientCarId));
      }

      const response = await apiClient.get<{
        message: string;
        data: FollowUpStatusData;
      }>(`/services/follow-up-status?${search.toString()}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /** Validity of services for every client (GET /services/follow-up-status). */
  async getCompanyFollowUpStatus(): Promise<
    ApiResponse<FollowUpStatusByClientData>
  > {
    try {
      const response = await apiClient.get<{
        message: string;
        data: FollowUpStatusByClientData;
      }>("/services/follow-up-status");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Delete a service-subservice link
  async deleteServiceSubServiceLink(
    relationshipId: number
  ): Promise<ApiResponse<boolean>> {
    try {
      await apiClient.delete(`/service-sub-services/${relationshipId}`);

      return {
        success: true,
        data: true,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },
};
